
import { Recycle, Leaf, FileSearch, Headphones, Cog, BookOpen, PenTool, HardHat, ChevronRight } from "lucide-react";

interface EngagementProps {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
}

const EngagementCard = ({ icon: Icon, title, description, color }: EngagementProps) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group">
      <div className="flex items-start mb-6">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${color} mr-6 shadow-md group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-heading font-bold text-fabre-gray mb-3 leading-tight">{title}</h3>
          <div className="w-12 h-1 bg-fabre-green rounded-full mb-4"></div>
        </div>
      </div>
      <p className="text-gray-600 leading-relaxed text-base">{description}</p>
      <div className="mt-6 flex items-center text-fabre-green font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <span className="text-sm">En savoir plus</span>
        <ChevronRight className="h-4 w-4 ml-1" />
      </div>
    </div>
  );
};

const EngagementsGrid = () => {
  const engagements: EngagementProps[] = [
    {
      icon: Recycle,
      title: "Respect des normes environnementales",
      description: "Nous respectons scrupuleusement toutes les normes et réglementations environnementales en vigueur pour assurer une gestion des déchets responsable et conforme.",
      color: "bg-gradient-to-br from-fabre-green to-fabre-darkgreen"
    },
    {
      icon: Leaf,
      title: "Avenir durable et économie circulaire",
      description: "Nous favorisons l'économie circulaire en maximisant la réutilisation des matériaux et en minimisant les déchets envoyés en décharge, contribuant ainsi à un avenir plus durable.",
      color: "bg-gradient-to-br from-fabre-teal to-fabre-darkteal"
    },
    {
      icon: FileSearch,
      title: "Transparence et traçabilité",
      description: "Nous assurons une traçabilité complète de tous les déchets traités, permettant à nos clients de suivre précisément le parcours de leurs déchets jusqu'à leur valorisation finale.",
      color: "bg-gradient-to-br from-fabre-gold to-yellow-600"
    },
    {
      icon: Headphones,
      title: "Service client réactif et de qualité",
      description: "Notre équipe est dédiée à fournir un service client exceptionnel, avec une réponse rapide et des solutions adaptées aux besoins spécifiques de chaque client.",
      color: "bg-gradient-to-br from-blue-500 to-blue-600"
    },
    {
      icon: Cog,
      title: "Valorisation des matériaux",
      description: "Nous mettons en œuvre des processus innovants pour maximiser la valorisation de tous types de matériaux, des plus simples aux plus complexes.",
      color: "bg-gradient-to-br from-purple-500 to-purple-600"
    },
    {
      icon: BookOpen,
      title: "Sensibilisation au tri du grand public",
      description: "Nous menons des actions de sensibilisation auprès du grand public pour promouvoir les bonnes pratiques de tri et de recyclage, contribuant à l'éducation environnementale.",
      color: "bg-gradient-to-br from-orange-500 to-orange-600"
    },
    {
      icon: PenTool,
      title: "Innovation technologique",
      description: "Nous investissons continuellement dans les nouvelles technologies pour améliorer nos processus de recyclage et offrir des solutions toujours plus efficaces et respectueuses de l'environnement.",
      color: "bg-gradient-to-br from-indigo-500 to-indigo-600"
    },
    {
      icon: HardHat,
      title: "Personnel formé aux risques",
      description: "Notre personnel bénéficie de formations régulières sur les aspects sécuritaires et environnementaux de notre activité, garantissant un niveau de compétence élevé.",
      color: "bg-gradient-to-br from-red-500 to-red-600"
    }
  ];

  return (
    <section className="py-20 bg-fabre-beige">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {engagements.map((engagement, index) => (
            <EngagementCard
              key={index}
              icon={engagement.icon}
              title={engagement.title}
              description={engagement.description}
              color={engagement.color}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EngagementsGrid;
